import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, Download, Layers, Paperclip, Package, Pencil, Trash2, X } from 'lucide-react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Heading,
  KeyValue,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Skeleton,
  StatusBadge,
  WorkflowTimeline,
  Badge,
  type StatusKey,
} from '@insolech/ui';
import {
  orderMutations,
  orderQueries,
  orderFileTypes,
  type OrderDetail,
  type OrderHistoryRow,
  type PaymentGroupDetail,
  type PaymentGroupOrderMember,
} from '@insolech/sdk';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/features/auth/AuthContext';
import { supabase } from '@/lib/supabase';
import { OrderLinesCard } from './OrderLinesCard';
import { OrderProductionCard } from './OrderProductionCard';
import { EditOrderLinePriceDialog } from './EditOrderLinePriceDialog';
import { CombineOrdersDialog } from './CombineOrdersDialog';
import { MarkPaidDialog } from './MarkPaidDialog';
import { AdminQrSection } from './AdminQrSection';
import { AdminReordersSection } from './AdminReordersSection';
import {
  isCustomLine,
  orderCategoryLabel,
  resolvePatientDisplay,
  shortProductLabel,
} from './productLabel';
import {
  canEditOrderAtStatus,
  isInProductionOrLater,
  IN_PRODUCTION_WARNING,
} from './orderEditMatrix';
import { orderFileIcon } from './orderFileIcon';
import { orderPatientGender } from './orderPatientGender';

/**
 * OrderDetailPage - single order's full view, wired against the live DB.
 *
 * Layout:
 *   - Top: back button + page header with status
 *   - 2-column body:
 *     · main:    line cards, status timeline, files, comments
 *     · sidebar: patient summary, billing, admin-only assignment + actions
 *
 * Realtime: order_messages and order_status_history are subscribed for live
 * updates while the page is mounted.
 */

const STATUS_TO_KEY: Record<string, StatusKey> = {
  draft: 'draft',
  submitted: 'submitted',
  in_review: 'in_review',
  in_production: 'in_production',
  in_delivery: 'in_delivery',
  delivered: 'delivered',
  cancelled: 'cancelled',
  returned: 'returned',
  on_hold: 'on_hold',
  assigned_to_provider: 'assigned_to_provider',
  quality_check: 'quality_check',
};

interface Props {
  /** When true, renders admin-only sidebar actions. */
  scope?: 'admin' | 'podiatrist';
}

export function OrderDetailPage({ scope = 'podiatrist' }: Props = {}) {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin, isPodiatrist, user } = useAuth();
  const qc = useQueryClient();

  const detail = useQuery({
    ...orderQueries.orderDetailQuery(supabase, id),
    enabled: !!id,
  });
  const history = useQuery({
    ...orderQueries.orderHistoryQuery(supabase, id),
    enabled: !!id,
  });
  const files = useQuery({
    ...orderQueries.orderFilesQuery(supabase, id),
    enabled: !!id,
  });

  // Realtime subscriptions
  React.useEffect(() => {
    if (!id) return;
    const channel = supabase
      .channel(`order-detail-${id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'order_messages', filter: `order_id=eq.${id}` },
        () => {
          void qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.messages(id) });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'order_status_history' },
        () => {
          // Invalidate history; cheap query.
          void qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.history(id) });
          void qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.detail(id) });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [id, qc]);

  if (!id) return <NotFound onBack={() => navigate(-1)} />;
  if (detail.isLoading) {
    return (
      <AppShell>
        <main className="px-6 py-6 max-w-[1400px] mx-auto space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </main>
      </AppShell>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <AppShell>
        <main className="px-6 py-6 max-w-[1400px] mx-auto">
          <Alert tone="error" title="Could not load order">
            {String(detail.error ?? 'Order not found.')}
          </Alert>
        </main>
      </AppShell>
    );
  }

  const order = detail.data;
  const adminScope = scope === 'admin' && isAdmin;
  // Who is allowed to see prices (line unit price, line total, billing
  // subtotal/tax/total) on this order detail page.
  //   - Admins always see prices.
  //   - The author of a direct-billed order sees them too (the podiatrist
  //     negotiated the price with the patient, so they need to confirm what
  //     the patient was quoted). The `isPodiatrist` guard is belt-and-braces
  //     so a pure agent who somehow ends up as orders.podiatrist_id (legacy
  //     row or admin reassignment) does not slip through, since pure agents
  //     have `isPodiatrist === false`.
  //   - Agents (without a podiatrist role) NEVER see prices, even on
  //     direct-billed orders they introduced; agent commission UX lives on
  //     /agent, not here. A podiatrist who is ALSO an agent still sees
  //     prices on their own direct-billed orders because their primary
  //     workspace is the podiatrist one.
  const canViewPrices =
    isAdmin ||
    (order.is_direct_billed &&
      isPodiatrist &&
      !!user?.id &&
      order.podiatrist_id === user.id);
  // The sidebar Billing card surfaces internal accounting (subtotal /
  // tax / total / Unbilled status). That view is admin-only, even on
  // direct-billed orders the podiatrist authored. Podiatrists already
  // confirm what the patient was quoted via the per-line price column
  // inside Order Summary (gated by canViewPrices above), so the
  // accounting roll-up stays admin-only to avoid implying agent
  // commission or tax treatment to the practitioner.
  const canViewBillingCard = isAdmin;
  // Ready-made insoles / orthopaedic materials / FITO shoes aren't tied to a
  // patient + prescription, so they get a product-led layout (no Files card,
  // category title instead of "Unknown patient").
  const nonCustom = !isCustomLine(order.lines[0]?.line_type);

  return (
    <AppShell>
      <main className="px-6 py-6 max-w-[1400px] mx-auto">
        {/* Breadcrumb */}
        <Button
          variant="ghost"
          size="sm"
          startIcon={<ArrowLeft className="h-4 w-4 rtl-flip" />}
          className="mb-4 -ms-2"
          onClick={() => navigate(adminScope ? '/admin/orders' : '/orders')}
        >
          Back to orders
        </Button>

        {nonCustom ? <CatalogOrderHeader order={order} /> : <OrderHeader order={order} />}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main column: order summary, activity, files (custom orders
              only), then the admin workflow cards stacked top-to-bottom
              in a single column (review -> production -> take payment)
              rather than zig-zagging between sidebar and main. The
              sidebar is now strictly patient + billing-summary context. */}
          <div className="lg:col-span-2 space-y-6">
            {nonCustom ? (
              <CatalogOrderSummaryCard
                order={order}
                isAdmin={isAdmin}
                canViewPrices={canViewPrices}
              />
            ) : (
              <OrderLinesCard order={order} canViewPrices={canViewPrices} />
            )}
            <TimelineCard history={history.data ?? []} loading={history.isLoading} />
            {nonCustom ? null : (
              <FilesCard
                order={order}
                files={files.data ?? []}
                loading={files.isLoading}
                isAdmin={isAdmin}
                uploaderId={user?.id ?? ''}
              />
            )}
            {isAdmin && !nonCustom ? (
              <OrderProductionCard order={order} isAdmin={adminScope} />
            ) : null}
            {/* NFC token operations: per-line QR / short URL oversight and the
                patient reorders that came in against this order. Admin-only,
                custom orders only (marketplace/OTS lines carry no tag). */}
            {isAdmin && !nonCustom ? (
              <>
                <AdminQrSection
                  orderId={order.id}
                  linesIndex={order.lines.map((l) => ({
                    id: l.id,
                    label: shortProductLabel(l),
                    eligible: isCustomLine(l.line_type),
                  }))}
                />
                <AdminReordersSection orderId={order.id} />
              </>
            ) : null}
            {/* Direct-billing payment card. Renders for any direct-billed
                order regardless of line family (drops the !nonCustom gate
                that the Production card uses, because marketplace / OTS
                orders can also be direct-billed). Admin-only: podiatrists
                see the order is direct-billed via the Clinic-column badge
                on the tracker but cannot mint payment links from their
                own surface. The state-machine gate keeps production
                blocked until the order is marked paid. */}
            {isAdmin && order.is_direct_billed ? (
              <DirectBillingPaymentCard order={order} />
            ) : null}
            {/* Reassignment double-billing guard: shows for any admin order whose
                zoho_invoice_blocked flag is set (an order reassigned to a clinic
                after the patient already paid). The override lives ONLY here. */}
            {isAdmin && order.zoho_invoice_blocked ? (
              <ZohoInvoiceBlockCard order={order} />
            ) : null}
          </div>

          {/* Sidebar: patient/order details + admin-only billing summary.
              Billing surfaces internal accounting (subtotal / tax /
              total / Unbilled status) and is admin-only via
              canViewBillingCard. Direct-billed podiatrists still see
              their per-line prices inside Order Summary (canViewPrices)
              so they are not blind to what the patient was quoted; the
              accounting roll-up is intentionally stricter than the
              per-line price view. The direct-billing payment card now
              lives in the main column next to Production, not here. */}
          <div className="space-y-6">
            {nonCustom ? <CatalogOrderInfoCard order={order} /> : <PatientCard order={order} />}
            {/* Billing card is admin-only even when the author of a direct-billed order can see per-line prices. */}
            {canViewBillingCard ? <BillingCard order={order} /> : null}
          </div>
        </div>
      </main>
    </AppShell>
  );
}

/** Header for non-custom (catalog) orders: product category as the title,
 *  not a patient name. */
function CatalogOrderHeader({ order }: { order: OrderDetail }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 pb-6 border-b border-border-subtle">
      <div className="flex items-start gap-4">
        <div className="h-14 w-14 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
          <Package className="h-6 w-6" />
        </div>
        <div>
          <Heading as="h1" size="h3">
            {orderCategoryLabel(order.lines[0])}
          </Heading>
          <div className="flex items-center gap-3 mt-2 text-sm text-text-secondary flex-wrap">
            <span className="font-mono text-text-primary">{order.order_number}</span>
            <span className="opacity-60">·</span>
            <span>Created {formatDate(order.created_at)}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end gap-3">
        <StatusBadge status={(STATUS_TO_KEY[order.status] ?? 'draft') as StatusKey} size="md" />
      </div>
    </div>
  );
}

/** "Order summary" for non-custom orders: the products ordered + quantities,
 *  with the optional per-line patient name, the line price (only when
 *  canViewPrices is true), and an admin-only pencil button to override the
 *  price on any status. */
function CatalogOrderSummaryCard({
  order,
  isAdmin,
  canViewPrices,
}: {
  order: OrderDetail;
  isAdmin: boolean;
  /** Hide the per-line price column entirely (price text + line total)
   *  when false. The admin pencil button is gated by isAdmin separately,
   *  which is a strictly stronger condition. */
  canViewPrices: boolean;
}) {
  const [editingLineId, setEditingLineId] = React.useState<string | null>(null);
  const editingLine = React.useMemo(
    () => order.lines.find((l) => l.id === editingLineId) ?? null,
    [order.lines, editingLineId],
  );
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Order summary</CardTitle>
        </CardHeader>
        <CardBody>
          <ul className="divide-y divide-border-subtle">
            {order.lines.map((l) => {
              const cfg = (l.configuration ?? {}) as Record<string, unknown>;
              const name =
                (cfg.display_label as string) ||
                (cfg.product_name as string) ||
                shortProductLabel(l);
              const code = cfg.product_code as string | undefined;
              return (
                <li key={l.id} className="py-3 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text-primary">{name}</p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-muted">
                      {code ? <span className="font-mono">{code}</span> : null}
                      {l.patient_name ? <span>Patient: {l.patient_name}</span> : null}
                      {/* Reorder cart R2: a FITO cross-sell line is a stock
                          product - it never enters production. Informational
                          only; no stock gating. */}
                      {cfg.source === 'reorder_cross_sell' && l.line_type === 'other_equipment' ? (
                        <span
                          className="inline-flex items-center rounded-full border border-border-subtle bg-sand-100 px-2 py-0.5 text-[11px] font-medium text-text-secondary"
                          data-testid="ships-from-stock-chip"
                        >
                          Ships from stock
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {canViewPrices ? (
                    <div className="shrink-0 flex flex-col items-end gap-0.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm tabular text-text-primary">
                          AED {l.unit_price_aed.toFixed(2)} × {l.quantity}
                        </span>
                        {isAdmin ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingLineId(l.id)}
                            aria-label="Edit line price"
                            title="Edit price (admin)"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        ) : null}
                      </div>
                      <span className="text-xs tabular font-medium text-text-secondary">
                        = AED {l.line_total_aed.toFixed(2)}
                      </span>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </CardBody>
      </Card>

      {editingLine ? (
        <EditOrderLinePriceDialog
          open={!!editingLine}
          order={order}
          line={editingLine}
          onClose={() => setEditingLineId(null)}
        />
      ) : null}
    </>
  );
}

/** Sidebar card for non-custom orders: clinic / ordered-by / patient name(s)
 *  + free-text order notes. Replaces the patient-centric card. */
function CatalogOrderInfoCard({ order }: { order: OrderDetail }) {
  const patientNames = Array.from(
    new Set(
      order.lines
        .map((l) => l.patient_name?.trim())
        .filter((n): n is string => !!n),
    ),
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>Order details</CardTitle>
      </CardHeader>
      <CardBody>
        <KeyValue
          items={[
            { label: 'Category', value: orderCategoryLabel(order.lines[0]) },
            { label: 'Clinic', value: order.clinic?.name ?? '-' },
            { label: 'Ordered by', value: order.podiatrist?.full_name ?? '-' },
            { label: 'Patient', value: patientNames.length ? patientNames.join(', ') : '-' },
            {
              label: 'Delivered on',
              value: order.actual_delivery_date ? formatDate(order.actual_delivery_date) : '-',
            },
          ]}
        />
        {order.notes?.trim() ? (
          <div className="pt-4 mt-4 border-t border-border-subtle">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-1">
              Notes
            </p>
            <p className="text-sm text-text-secondary whitespace-pre-wrap">{order.notes}</p>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function OrderHeader({ order }: { order: OrderDetail }) {
  // Record-less orders (reorder-by-name + every migrated order) carry the name
  // on order_lines.patient_name, not a patients row. Resolve via the same
  // header->line fallback the tracker/dashboard use.
  const patientName = resolvePatientDisplay(order).name;
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 pb-6 border-b border-border-subtle">
      <div className="flex items-start gap-4">
        <Avatar name={patientName ?? 'Unknown'} size="xl" />
        <div>
          <Heading as="h1" size="h3">
            {patientName ?? 'Unknown patient'}
          </Heading>
          <div className="flex items-center gap-3 mt-2 text-sm text-text-secondary flex-wrap">
            <span className="font-mono text-text-primary">{order.order_number}</span>
            <span className="opacity-60">·</span>
            <span>Created {formatDate(order.created_at)}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end gap-3">
        <StatusBadge status={(STATUS_TO_KEY[order.status] ?? 'draft') as StatusKey} size="md" />
      </div>
    </div>
  );
}

// LinesCard moved to ./OrderLinesCard.tsx - Phase 4b enhanced summary + drawer.

function FilesCard({
  order,
  files,
  loading,
  isAdmin,
  uploaderId,
}: {
  order: OrderDetail;
  files: orderQueries.OrderFileRow[];
  loading: boolean;
  isAdmin: boolean;
  uploaderId: string;
}) {
  const qc = useQueryClient();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  // Documents attach to the order's primary line (the wizard convention). The
  // matrix (admin any status; clinic member draft/submitted) mirrors the
  // server-side RLS/RPC; the RPC is authoritative, this only shapes the UI.
  const primaryLine = order.lines[0];
  const canMutate =
    !!primaryLine && canEditOrderAtStatus(isAdmin, order.status, primaryLine.status);
  const showProdWarning = isAdmin && isInProductionOrLater(order.status);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.files(order.id) });
    void qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.detail(order.id) });
  };

  // Batch upload: validate + upload each picked file independently so one bad
  // file never aborts the rest, then report a clear per-file summary.
  const onFilesChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (picked.length === 0 || !primaryLine) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    const failures: string[] = [];
    let uploaded = 0;
    for (const file of picked) {
      const v = orderFileTypes.validateOrderFile(file);
      if (!v.ok) {
        failures.push(`${file.name}: ${v.reason}`);
        continue;
      }
      try {
        await orderMutations.uploadOrderFile(supabase, {
          bucket: 'scans',
          fileKind: 'additional',
          file,
          clinicId: order.clinic_id,
          orderId: order.id,
          orderLineId: primaryLine.id,
          uploaderId,
        });
        uploaded += 1;
      } catch (err) {
        failures.push(`${file.name}: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    invalidate();
    setBusy(false);
    if (failures.length === 0) {
      setNotice(uploaded === 1 ? '1 document attached.' : `${uploaded} documents attached.`);
    } else {
      setError(
        `${uploaded} attached, ${failures.length} could not be attached:\n${failures.join('\n')}`,
      );
    }
  };

  const onArchive = async (fileId: string) => {
    if (
      !window.confirm(
        'Remove this document from the order? It will no longer be attached to the supplier email.',
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await orderMutations.archiveOrderFile(supabase, fileId, true);
      invalidate();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-2">
        <CardTitle>Files</CardTitle>
        {canMutate ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={orderFileTypes.ORDER_FILE_ACCEPT}
              className="hidden"
              onChange={onFilesChosen}
            />
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="h-4 w-4 me-1.5" />
              Attach documents
            </Button>
          </>
        ) : null}
      </CardHeader>
      <CardBody>
        {showProdWarning ? (
          <Alert tone="warning" className="mb-3">
            {IN_PRODUCTION_WARNING}
          </Alert>
        ) : null}
        {error ? (
          <Alert tone="error" title="Could not attach some documents" className="mb-3">
            <span className="whitespace-pre-line">{error}</span>
          </Alert>
        ) : null}
        {notice ? (
          <Alert tone="success" className="mb-3">
            {notice}
          </Alert>
        ) : null}
        {loading ? (
          <Skeleton className="h-6 w-full" />
        ) : files.length === 0 ? (
          <p className="text-sm text-text-muted">No files attached to this order yet.</p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {files.map((f) => (
              <FileRow
                key={f.id}
                file={f}
                canArchive={canMutate}
                busy={busy}
                onArchive={onArchive}
              />
            ))}
          </ul>
        )}
        <p className="mt-3 text-xs text-text-muted">
          Attached documents are included when the order is emailed to the supplier.
        </p>
      </CardBody>
    </Card>
  );
}

function FileRow({
  file,
  canArchive,
  busy,
  onArchive,
}: {
  file: orderQueries.OrderFileRow;
  canArchive?: boolean;
  busy?: boolean;
  onArchive?: (id: string) => void;
}) {
  const [opening, setOpening] = React.useState(false);
  const onOpen = async () => {
    setOpening(true);
    const url = await orderQueries.getSignedUrlFor(supabase, file.storage_bucket, file.storage_path);
    setOpening(false);
    if (url) window.open(url, '_blank', 'noopener');
  };
  const { Icon, label } = orderFileIcon(file.file_name, file.mime_type);
  return (
    <li className="py-3 flex items-center gap-3">
      <div className="h-9 w-9 rounded-md bg-brand-50 text-brand-600 flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-primary truncate">{file.file_name}</p>
        <p className="text-xs text-text-muted">
          {label} · {Math.round(file.file_size_bytes / 1024)} KB
        </p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Download"
        onClick={onOpen}
        disabled={opening}
      >
        <Download className="h-4 w-4" />
      </Button>
      {canArchive && onArchive ? (
        <Button
          variant="ghost"
          size="icon"
          aria-label="Remove document"
          disabled={busy}
          onClick={() => onArchive(file.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ) : null}
    </li>
  );
}

function PatientCard({ order }: { order: OrderDetail }) {
  // R27 - compute the 12-month renewal date from actual delivery.
  // Used by the monthly renewal-summary email digest (R18 cron).
  // Blank until the order is delivered.
  const renewalDate = order.actual_delivery_date
    ? (() => {
        const d = new Date(order.actual_delivery_date);
        d.setFullYear(d.getFullYear() + 1);
        return formatDate(d.toISOString());
      })()
    : null;

  const sexLabel = (g: string | null | undefined) => {
    if (!g) return '-';
    if (g === 'male') return 'Male';
    if (g === 'female') return 'Female';
    if (g === 'other') return 'Other';
    if (g === 'prefer_not_to_say') return 'Prefer not to say';
    return g;
  };

  // Record-less orders carry the name on the line, not a patients row - resolve
  // via the same header->line fallback the tracker/dashboard use.
  const patientName = resolvePatientDisplay(order).name;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Patient</CardTitle>
      </CardHeader>
      <CardBody>
        <div className="flex items-center gap-3 pb-4 border-b border-border-subtle">
          <Avatar name={patientName ?? 'Unknown'} size="md" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-text-primary truncate">
              {patientName ?? '-'}
            </p>
            <p className="text-xs text-text-muted font-mono">
              {order.lines[0]?.short_code ?? '-'}
            </p>
          </div>
        </div>
        <KeyValue
          className="pt-4"
          items={[
            // R27 - sex / email / phone right above the clinic line so
            // admin can contact the patient without bouncing to /patients.
            // Record first, then the primary line's configuration.gender, so a
            // record-less order shows the value the edit dialog just saved.
            { label: 'Sex', value: sexLabel(orderPatientGender(order)) },
            { label: 'Email', value: order.patient?.email ?? '-' },
            { label: 'Phone', value: order.patient?.phone ?? '-' },
            { label: 'Clinic', value: order.clinic?.name ?? '-' },
            { label: 'Podiatrist', value: order.podiatrist?.full_name ?? '-' },
            // Expected delivery removed - already shown in the orders tracker.
            {
              label: 'Delivered on',
              value: order.actual_delivery_date ? formatDate(order.actual_delivery_date) : '-',
            },
            // R27 - 12-month renewal date. Drives the monthly
            // renewal-summary email digest.
            {
              label: 'Renewal date',
              value: renewalDate ?? '-',
            },
          ]}
        />
      </CardBody>
    </Card>
  );
}

function TimelineCard({
  history,
  loading,
}: {
  history: OrderHistoryRow[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Activity</CardTitle>
        </CardHeader>
        <CardBody>
          <Skeleton className="h-32 w-full" />
        </CardBody>
      </Card>
    );
  }
  if (history.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Activity</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="text-sm text-text-muted">No transitions recorded yet.</p>
        </CardBody>
      </Card>
    );
  }
  const last = history[history.length - 1];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity</CardTitle>
      </CardHeader>
      <CardBody>
        <WorkflowTimeline
          events={history.map((h) => ({
            id: h.id,
            fromStatus: (h.from_status ? STATUS_TO_KEY[h.from_status] : null) ?? null,
            toStatus: STATUS_TO_KEY[h.to_status] ?? 'draft',
            changedAt: formatDate(h.changed_at),
            ...(h.actor?.full_name
              ? { changedBy: { name: h.actor.full_name, role: '' } }
              : {}),
            ...(h.reason ? { reason: h.reason } : {}),
            isCurrent: last !== undefined && h.id === last.id,
          }))}
        />
      </CardBody>
    </Card>
  );
}

/**
 * Card for direct-billed orders. Visible to admins AND the order's
 * author (the podiatrist) so the practitioner can send the patient a
 * Stripe payment link from their own /orders detail view. Surfaces the
 * payment status, a "Send payment link" / "Copy payment link" button
 * backed by the create-direct-billing-checkout Edge Function, and an
 * admin-only "Mark as paid" fallback for cash / bank-transfer flows.
 *
 * State machine:
 *   - direct_billing_paid_at IS NOT NULL -> single paid-on row, no buttons.
 *   - stripe_checkout_session_id IS NULL -> primary button reads
 *     "Send payment link". Clicking mints a Stripe session, copies the
 *     URL to the clipboard, and invalidates the order detail query so
 *     the card flips to the "Copy" state on next render.
 *   - stripe_checkout_session_id IS NOT NULL -> button reads "Copy
 *     payment link" and either re-copies a freshly-cached URL (no Stripe
 *     round trip) or re-invokes the Edge Function (which returns the
 *     URL for the existing session). The hosted Stripe URL eventually
 *     expires; users can mint a fresh one by re-clicking either label.
 *
 * The state-machine trigger short-circuits direct-billed orders into
 * production only when direct_billing_paid_at IS NOT NULL, so the
 * "Mark as paid" admin shortcut is what currently unblocks dispatch
 * (the Stripe webhook will stamp the same column automatically once
 * the patient pays online).
 */
/**
 * ZohoInvoiceBlockCard — admin-only. Renders only when order.zoho_invoice_blocked
 * is set (an order reassigned to a clinic AFTER the patient already paid it via
 * direct billing). zoho-create-invoice refuses to invoice the clinic while the
 * flag is set. This card is the ONLY place the block can be cleared, via the
 * admin-only override_order_zoho_block RPC.
 */
function ZohoInvoiceBlockCard({ order }: { order: OrderDetail }) {
  const qc = useQueryClient();
  const [err, setErr] = React.useState<string | null>(null);
  const clearMut = useMutation({
    mutationFn: async () => {
      await orderMutations.overrideOrderZohoBlock(supabase, { orderId: order.id, blocked: false });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.detail(order.id) });
      void qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.lists() });
    },
    onError: (e) =>
      setErr(
        e instanceof Error
          ? e.message
          : e && typeof e === 'object' && 'message' in e
            ? String((e as Record<string, unknown>).message)
            : String(e),
      ),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Clinic invoicing blocked</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <Alert tone="warning" title="This order will not be invoiced to its clinic">
          It was reassigned to a clinic after the patient had already paid it directly, so Zoho
          invoicing is blocked to prevent double billing. Only clear this if the clinic genuinely owes
          for this order.
        </Alert>
        {err ? (
          <Alert tone="error" title="Could not clear the block">
            {err}
          </Alert>
        ) : null}
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setErr(null);
            clearMut.mutate();
          }}
          disabled={clearMut.isPending}
        >
          {clearMut.isPending ? 'Clearing...' : 'Clear invoice block'}
        </Button>
      </CardBody>
    </Card>
  );
}

function DirectBillingPaymentCard({ order }: { order: OrderDetail }) {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const isPaid = !!order.direct_billing_paid_at;
  const isInGroup = !!order.payment_group_id;
  // Short-lived in-session cache for URLs that we minted via the Edge
  // Function during this page lifetime but have not yet seen reflected back
  // from the DB. The persisted column (orders.stripe_checkout_url or
  // direct_billing_payment_groups.stripe_checkout_url, see migration
  // 20261125000000) is the primary source of truth; this cache only covers
  // the brief window between a mint and the query invalidation refresh.
  const [cachedCheckoutUrl, setCachedCheckoutUrl] = React.useState<string | null>(null);
  const [statusLine, setStatusLine] = React.useState<string | null>(null);
  const [combineOpen, setCombineOpen] = React.useState(false);
  const [combineMode, setCombineMode] = React.useState<'create' | 'edit'>('create');

  // Load the payment group whenever the order is part of one. Returns the
  // sibling orders + the group's stripe session id and URL so this card can
  // drive its Send/Copy button from the GROUP instead of the order itself.
  const paymentGroupQuery = useQuery({
    queryKey: ['orders', 'payment-group', order.payment_group_id ?? null],
    queryFn: () =>
      orderMutations.getPaymentGroup(supabase, order.payment_group_id as string),
    enabled: !!order.payment_group_id,
  });
  const paymentGroup: PaymentGroupDetail | null = paymentGroupQuery.data ?? null;
  const isGroupPaid =
    paymentGroup !== null && (paymentGroup.status === 'paid' || !!paymentGroup.paid_at);

  // In group mode the Stripe session id lives on the group row; outside of
  // a group it lives directly on the order. The hasSession + send/copy
  // labels switch automatically based on which one is populated.
  const effectiveSessionId = isInGroup
    ? paymentGroup?.stripe_checkout_session_id ?? null
    : order.stripe_checkout_session_id;
  const hasSession = !!effectiveSessionId;

  // Persisted URL (preferred) plus the in-memory cache (fallback). When
  // both are null we have never minted a session and the button must show
  // "Send payment link" and hit the Edge Function. When either is set,
  // "Copy payment link" reads the URL straight from state and copies it to
  // the clipboard without round-tripping to the Edge Function.
  const persistedCheckoutUrl: string | null = isInGroup
    ? paymentGroup?.stripe_checkout_url ?? null
    : order.stripe_checkout_url;
  const effectiveCheckoutUrl: string | null =
    persistedCheckoutUrl ?? cachedCheckoutUrl;

  // Auto-clear the inline status text after a few seconds so the card does
  // not stay in the "copied" state forever (matches conventional toast UX).
  React.useEffect(() => {
    if (!statusLine) return;
    const t = window.setTimeout(() => setStatusLine(null), 4000);
    return () => window.clearTimeout(t);
  }, [statusLine]);

  const copyToClipboard = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setStatusLine('Payment link copied. Share it with the patient.');
    } catch {
      // Fallback when navigator.clipboard is unavailable (older browsers,
      // insecure origin). Surface the URL so the user can copy it by hand.
      setStatusLine(`Could not copy automatically. Link: ${url}`);
    }
  };

  const createCheckoutMut = useMutation({
    mutationFn: async () => {
      // In group mode, regenerate against the GROUP id (keeps the link
      // tied to the aggregated total). Outside of a group, hit the
      // single-order Edge Function path.
      //
      // The Edge Function's top-level input gate requires order_id or
      // order_ids on EVERY request (payment_group_id alone returns 400).
      // For the group branch we therefore forward the current member ids
      // alongside payment_group_id; the function routes that combination
      // to its explicit-regeneration path.
      if (isInGroup && order.payment_group_id) {
        const memberIds = (paymentGroup?.orders ?? []).map((o) => o.id);
        // Defence-in-depth: if the group hydration query has not finished
        // we fall back to just the current order id (Edge Function will
        // 400 on group-mismatch, surfacing the issue rather than silently
        // hitting the unaffiliated branch).
        const ids = memberIds.length > 0 ? memberIds : [order.id];
        const res = await orderMutations.regenerateGroupPaymentLink(
          supabase,
          order.payment_group_id,
          ids,
        );
        return { checkout_url: res.checkoutUrl, regenerated: false };
      }
      const r = await orderMutations.createDirectBillingCheckout(supabase, order.id);
      return {
        checkout_url: r.checkout_url,
        regenerated: !!r.regenerated,
        previous_amount_aed: r.previous_amount_aed ?? null,
        previous_link_cancelled: r.previous_link_cancelled ?? null,
      };
    },
    onSuccess: async (result) => {
      setCachedCheckoutUrl(result.checkout_url);
      await copyToClipboard(result.checkout_url);
      // MONEY SAFETY: the stored link charged a different (stale) amount, so it
      // was cancelled and replaced. Say so explicitly - the old link may
      // already be with the clinic, and silently swapping it would leave them
      // holding a dead link with no explanation.
      if (result.regenerated) {
        const was =
          typeof result.previous_amount_aed === 'number'
            ? `AED ${result.previous_amount_aed.toFixed(2)}`
            : 'a different amount';
        const cancelNote =
          result.previous_link_cancelled === false
            ? '\n\nWARNING: the old link could NOT be cancelled automatically. Cancel that Checkout session in the Stripe dashboard now.'
            : '\n\nThe old link has been cancelled and can no longer be paid.';
        alert(
          `Price changed since the last payment link was created (it charged ${was}).\n\n` +
            `A NEW link for the current total has been generated and copied to your clipboard. ` +
            `Re-send it to the clinic.${cancelNote}`,
        );
      }
      void qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.detail(order.id) });
      if (order.payment_group_id) {
        void qc.invalidateQueries({
          queryKey: ['orders', 'payment-group', order.payment_group_id],
        });
        // Invalidate siblings too so their banners get the new session id.
        for (const sib of paymentGroup?.orders ?? []) {
          if (sib.id === order.id) continue;
          void qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.detail(sib.id) });
        }
      }
    },
    onError: (e) => {
      console.error('[direct billing checkout]', e);
      const msg = e instanceof Error ? e.message : String(e);
      // Zero-total guard (create-direct-billing-checkout refuses to mint a
      // free checkout): point the admin at the actual next step instead of
      // leaving a dead end. Orders created before the wizard enforced
      // direct-billing prices carry 0.00 totals until priced here.
      const hint = /no positive total/i.test(msg)
        ? '\n\nSet the price first: use the Edit price (pencil) control on the order line, then send the payment link again.'
        : '';
      alert(`Could not create payment link:\n${msg}${hint}`);
    },
  });

  const handleSendOrCopy = async () => {
    // Fast path is GROUP-ONLY. For a single order we must NOT hand back the
    // stored URL blindly: the order total can change after the link is minted
    // (admin edits line price / quantity / discount), and the stored Stripe
    // session keeps charging the OLD amount - a clinic could pay a stale
    // price. The Edge Function is the authority: it compares the session's
    // amount against the current total and either returns the SAME session
    // (no churn when nothing changed) or expires the stale one and mints a
    // fresh link. One admin-triggered round trip is a fair price for never
    // serving a wrong amount.
    if (isInGroup && effectiveCheckoutUrl) {
      await copyToClipboard(effectiveCheckoutUrl);
      return;
    }
    // Slow path: no URL persisted yet. Mint via the Edge Function (single
    // -order or group, see createCheckoutMut.mutationFn). This is also the
    // legacy-data fallback for rows minted before migration
    // 20261125000000 added the stripe_checkout_url column: a single Edge
    // Function call retrieves the URL from Stripe and stamps the column,
    // so the very next click takes the fast path.
    createCheckoutMut.mutate();
  };

  // "Mark as paid" opens a dialog that requires a payment method; the
  // mutation itself lives in MarkPaidDialog (RPC with actor + method +
  // audit trail, item 29). The one-click bare UPDATE is gone.
  const [markPaidOpen, setMarkPaidOpen] = React.useState(false);

  // Label/state derives from the URL availability rather than just the
  // session id: when stripe_checkout_url is persisted we can copy without
  // a round trip, so the button reads "Copy payment link" immediately.
  // Falls back to hasSession for legacy rows where the session id exists
  // but the URL column has not yet been backfilled (first click takes the
  // slow path and stamps the column for next time).
  const canCopyDirectly = !!effectiveCheckoutUrl;
  const sendLabel = canCopyDirectly || hasSession ? 'Copy payment link' : 'Send payment link';
  const sendPendingLabel = canCopyDirectly || hasSession ? 'Copying...' : 'Generating link...';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Direct billing</CardTitle>
      </CardHeader>
      <CardBody>
        {/* Payment discrepancy banner: stamped by the stripe-webhook for
            amount mismatches and DOUBLE COLLECTIONS (a payment landing on an
            already-paid order, item 30). Every admin is also notified, but
            the order page itself must scream. */}
        {order.payment_mismatch_at ? (
          <Alert tone="error" title="Payment discrepancy - reconcile before invoicing">
            {order.payment_mismatch_note ??
              `A payment discrepancy was recorded on ${order.payment_mismatch_at}.`}
          </Alert>
        ) : null}
        {/* Combined-group banner sits at the very top so admins always see
            the group context regardless of paid / unpaid state below. */}
        {isInGroup ? (
          <CombinedGroupBanner
            order={order}
            paymentGroup={paymentGroup}
            isLoading={paymentGroupQuery.isLoading}
            isError={paymentGroupQuery.isError}
            isAdmin={isAdmin}
            onOpenRegenerate={() => {
              setCombineMode('edit');
              setCombineOpen(true);
            }}
          />
        ) : null}

        {isPaid ? (
          <div className={isInGroup ? 'mt-4 flex items-center gap-2 text-sm' : 'flex items-center gap-2 text-sm'}>
            <span
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-success-100 text-success-700"
              aria-hidden
            >
              <Check className="h-3.5 w-3.5" />
            </span>
            <span className="text-text-secondary">
              Direct billing paid on{' '}
              <span className="text-text-primary font-medium">
                {formatDateTime(order.direct_billing_paid_at as string)}
              </span>
            </span>
          </div>
        ) : (
          <div className={isInGroup ? 'mt-4 space-y-3' : 'space-y-3'}>
            <div className="flex items-center gap-2 text-sm">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full bg-warning-500"
                aria-hidden
              />
              <span className="text-text-secondary">
                {isInGroup
                  ? 'Combined direct billing pending payment'
                  : 'Direct billing pending payment'}
              </span>
            </div>
            <p className="text-xs text-text-muted">
              Production is blocked until the order is marked as paid.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  void handleSendOrCopy();
                }}
                disabled={createCheckoutMut.isPending}
              >
                {createCheckoutMut.isPending ? sendPendingLabel : sendLabel}
              </Button>
              {statusLine ? (
                <p className="text-xs text-success-700">{statusLine}</p>
              ) : hasSession ? (
                <p className="text-xs text-text-muted">
                  {isInGroup
                    ? 'A combined payment link has been generated. Click to copy it again.'
                    : 'A payment link was previously generated. Click to copy it again.'}
                </p>
              ) : (
                <p className="text-xs text-text-muted">
                  No payment link generated yet.
                </p>
              )}
              {/* Combine entry point - admin only, single-order context, unpaid. */}
              {isAdmin && !isInGroup ? (
                <Button
                  size="sm"
                  variant="secondary"
                  startIcon={<Layers className="h-4 w-4" />}
                  onClick={() => {
                    setCombineMode('create');
                    setCombineOpen(true);
                  }}
                >
                  Combine with other orders
                </Button>
              ) : null}
              {isAdmin ? (
                <div className="border-t border-border-subtle pt-2">
                  <p className="text-xs text-text-muted">
                    or manually mark paid (cash / bank transfer):
                  </p>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="mt-2"
                    onClick={() => setMarkPaidOpen(true)}
                    disabled={isInGroup && !isGroupPaid}
                  >
                    Mark as paid
                  </Button>
                  {isInGroup && !isGroupPaid ? (
                    <p className="mt-1 text-xs text-text-muted">
                      Remove this order from the group first to mark it paid individually.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* Mark-as-paid dialog (method + note -> RPC, item 29) */}
        {isAdmin ? (
          <MarkPaidDialog
            open={markPaidOpen}
            orderId={order.id}
            orderNumber={order.order_number}
            totalAedLabel={`AED ${order.total_aed.toFixed(2)}`}
            hadStripeSession={!!order.stripe_checkout_session_id}
            onClose={() => setMarkPaidOpen(false)}
          />
        ) : null}

        {/* Combine / regenerate dialog */}
        {isAdmin ? (
          <CombineOrdersDialog
            open={combineOpen}
            onOpenChange={setCombineOpen}
            currentOrderId={order.id}
            currentOrderNumber={order.order_number}
            currentOrderPatientName={order.patient?.full_name ?? null}
            currentOrderTotalAed={order.total_aed}
            mode={combineMode}
            existingGroup={
              combineMode === 'edit' && paymentGroup
                ? {
                    id: paymentGroup.id,
                    total_aed: paymentGroup.total_aed,
                    orders: paymentGroup.orders,
                  }
                : null
            }
            onSuccess={() => {
              // Close after a beat so the success Alert is visible.
              window.setTimeout(() => setCombineOpen(false), 1200);
            }}
          />
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * CombinedGroupBanner - top-of-card block surfaced when an order is part of
 * a payment_group. Shows the group total, member count, sibling list, and
 * admin-only actions (regenerate link, remove this order from the group,
 * remove an individual sibling from the group). Read-only when the group
 * has already been paid.
 */
function CombinedGroupBanner({
  order,
  paymentGroup,
  isLoading,
  isError,
  isAdmin,
  onOpenRegenerate,
}: {
  order: OrderDetail;
  paymentGroup: PaymentGroupDetail | null;
  isLoading: boolean;
  isError: boolean;
  isAdmin: boolean;
  onOpenRegenerate: () => void;
}) {
  const qc = useQueryClient();
  const [confirmRemoveId, setConfirmRemoveId] = React.useState<string | null>(null);

  const removeMut = useMutation({
    mutationFn: async (orderId: string) => {
      await orderMutations.uncombineOrderFromGroup(supabase, orderId);
      return orderId;
    },
    onSuccess: (removedId) => {
      // Invalidate the removed order's detail, every sibling's detail (so
      // their banners disappear when the group dissolves), the group
      // query, and the combinable picker (the freed order should show up
      // in it again).
      void qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.detail(removedId) });
      for (const sib of paymentGroup?.orders ?? []) {
        if (sib.id === removedId) continue;
        void qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.detail(sib.id) });
      }
      if (paymentGroup) {
        void qc.invalidateQueries({
          queryKey: ['orders', 'payment-group', paymentGroup.id],
        });
      }
      void qc.invalidateQueries({
        queryKey: ['orders', 'combinable-direct-billed'],
      });
      void qc.invalidateQueries({ queryKey: orderQueries.ordersKeys.lists() });
      setConfirmRemoveId(null);
    },
    onError: (e) => {
      alert(
        `Could not remove order from the group: ${e instanceof Error ? e.message : String(e)}`,
      );
    },
  });

  if (isLoading) {
    return (
      <div className="rounded-md border border-brand-200 bg-brand-50 p-3 space-y-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-3 w-full" />
      </div>
    );
  }

  if (isError || !paymentGroup) {
    return (
      <Alert tone="warning" title="Combined payment group">
        Could not load the payment group details. Try refreshing the page.
      </Alert>
    );
  }

  const isGroupPaid = paymentGroup.status === 'paid' || !!paymentGroup.paid_at;
  const memberCount = paymentGroup.orders.length;
  const orderToConfirm: PaymentGroupOrderMember | null =
    confirmRemoveId !== null
      ? paymentGroup.orders.find((o) => o.id === confirmRemoveId) ?? null
      : null;

  return (
    <div
      className={
        isGroupPaid
          ? 'rounded-md border border-success-200 bg-success-50 p-3 space-y-3'
          : 'rounded-md border border-brand-200 bg-brand-50 p-3 space-y-3'
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <Layers
          className={isGroupPaid ? 'h-4 w-4 text-success-700' : 'h-4 w-4 text-brand-700'}
          aria-hidden="true"
        />
        <span className="font-semibold text-text-primary">
          {isGroupPaid ? 'Combined payment group (paid)' : 'Combined payment group'}
        </span>
        <Badge variant={isGroupPaid ? 'success' : 'brand'} size="sm">
          {memberCount} {memberCount === 1 ? 'order' : 'orders'}
        </Badge>
        {isGroupPaid && paymentGroup.paid_at ? (
          <Badge variant="success" size="sm">
            Paid {formatDateTime(paymentGroup.paid_at)}
          </Badge>
        ) : null}
      </div>

      <p className="text-sm text-text-secondary">
        Total{' '}
        <span className="font-medium text-text-primary tabular-nums">
          AED {paymentGroup.total_aed.toFixed(2)}
        </span>
      </p>

      <ul className="divide-y divide-border-subtle rounded-md border border-border-subtle bg-surface-elevated">
        {paymentGroup.orders.map((sib) => {
          const isCurrent = sib.id === order.id;
          return (
            <li
              key={sib.id}
              className="flex items-center gap-3 px-3 py-2 text-sm"
            >
              <div className="flex-1 min-w-0">
                {isCurrent ? (
                  <span className="font-medium text-text-muted">
                    {sib.order_number}{' '}
                    <span className="text-xs">(this order)</span>
                  </span>
                ) : (
                  <Link
                    to={`/admin/orders/${sib.id}`}
                    className="font-medium text-brand-700 hover:underline"
                  >
                    {sib.order_number}
                  </Link>
                )}
                <span className="ms-2 text-text-secondary">
                  {sib.patient?.full_name ?? '-'}
                </span>
              </div>
              <span className="tabular-nums text-text-primary">
                AED {sib.total_aed.toFixed(2)}
              </span>
              {isAdmin && !isGroupPaid && !isCurrent ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="px-2"
                  onClick={() => setConfirmRemoveId(sib.id)}
                  aria-label={`Remove order ${sib.order_number} from group`}
                  title="Remove from group"
                >
                  <X className="h-4 w-4" />
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>

      {isAdmin && !isGroupPaid ? (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            variant="secondary"
            size="sm"
            onClick={onOpenRegenerate}
          >
            Regenerate payment link
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-error-700 hover:text-error-800"
            onClick={() => setConfirmRemoveId(order.id)}
          >
            Remove this order from group
          </Button>
        </div>
      ) : null}

      {/* Confirm remove modal */}
      <Modal
        open={confirmRemoveId !== null}
        onOpenChange={(next) => {
          if (!next) setConfirmRemoveId(null);
        }}
      >
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>
              Remove order{' '}
              {orderToConfirm?.order_number ?? ''} from the combined group?
            </ModalTitle>
            <ModalDescription>
              The order will go back to its individual unpaid state. If a
              Stripe payment link has already been generated for the group,
              it will be invalidated and you will need to regenerate it for
              the remaining orders.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {removeMut.isError ? (
              <Alert tone="error" title="Could not remove order">
                {removeMut.error instanceof Error
                  ? removeMut.error.message
                  : 'Unexpected error removing the order from its group.'}
              </Alert>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmRemoveId(null)}
              disabled={removeMut.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              isLoading={removeMut.isPending}
              onClick={() => {
                if (confirmRemoveId) removeMut.mutate(confirmRemoveId);
              }}
            >
              {removeMut.isPending ? 'Removing...' : 'Remove from group'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}

function BillingCard({ order }: { order: OrderDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing</CardTitle>
      </CardHeader>
      <CardBody>
        <KeyValue
          layout="horizontal"
          items={[
            {
              // Read prices_include_vat, NEVER is_direct_billed. The two are
              // deliberately decoupled: reassign_order_billing flips the billing
              // audience without re-pricing the lines, and every order created
              // before migration 20270222000000 is direct-billed with an
              // EXCLUSIVE basis. Branching on is_direct_billed would label those
              // "VAT included" while VAT is still being added on top.
              label: order.prices_include_vat ? 'Net of VAT' : 'Subtotal',
              value: <span className="tabular">AED {order.subtotal_aed.toFixed(2)}</span>,
            },
            {
              label: order.prices_include_vat ? 'VAT (5%, included)' : 'VAT (5%)',
              value: <span className="tabular">AED {order.tax_aed.toFixed(2)}</span>,
            },
            {
              label: 'Total',
              value: (
                <span className="tabular font-semibold">AED {order.total_aed.toFixed(2)}</span>
              ),
            },
            {
              label: 'Status',
              value: <Badge variant="muted">Unbilled</Badge>,
            },
          ]}
        />
      </CardBody>
    </Card>
  );
}

function NotFound({ onBack }: { onBack: () => void }) {
  return (
    <AppShell>
      <main className="p-8">
        <Heading as="h1" size="h3">
          Order not found
        </Heading>
        <Button onClick={onBack} className="mt-4">
          Back
        </Button>
      </main>
    </AppShell>
  );
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return s;
  }
}

/** Day + time, used by the Direct-billing card for the paid timestamp. */
function formatDateTime(s: string): string {
  try {
    return new Date(s).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return s;
  }
}


